import { Form } from '@mairistem/composants';
import { useUpdateEffect } from '@mairistem/tools';
import _ from 'lodash';
import React from 'react';

import type { ChequeType } from '@/types';

import BanqueDropdown from './BanqueDropdown';
import Montant from './Montant';

interface ChequeProps {
  disabled: boolean;
  payeur: string;
  reste: number;
  numero: string;
  banque: string;
  tireur: string;
  montant: number;
  onChange: (value: ChequeType, complet: boolean) => void;
  onError?: (error: string) => void;
}

const Cheque = (props: ChequeProps) => {
  const {
    numero: numeroProps,
    banque: banqueProps,
    tireur: tireurProps,
    montant: montantProps,
    disabled,
    payeur,
    reste,
    onChange,
    onError,
  } = props;

  const [numero, setNumero] = React.useState<string>(numeroProps || '');
  const [banque, setBanque] = React.useState<string>(banqueProps || '');
  const [tireur, setTireur] = React.useState<string>(tireurProps || '');
  const [montant, setMontant] = React.useState<number>(montantProps || 0);

  const handleChange = React.useCallback((value, setter) => {
    setter(value);
  }, []);

  const handleTireurKeyDown = React.useCallback(
    (event) => {
      const { key } = event;

      if (key === '+') {
        setTireur(payeur);
        event.preventDefault();
      }
    },
    [payeur],
  );

  const handleIconKeyDown = React.useCallback(
    (event: React.KeyboardEvent) => {
      if (event.key === 'Enter' || event.key === ' ') {
        handleChange(payeur, setTireur);
        event.preventDefault();
      }
    },
    [handleChange, payeur],
  );

  const isComplet = React.useCallback(
    () =>
      !_.isEmpty(numero) &&
      !_.isEmpty(banque) &&
      !_.isEmpty(tireur) &&
      montant > 0 &&
      montant <= reste,
    [numero, banque, tireur, montant, reste],
  );

  useUpdateEffect(() => {
    if (!disabled) {
      onChange(
        {
          ...(numero !== '' && { numero }),
          ...(banque !== '' && { banque }),
          ...(tireur !== '' && { tireur }),
          ...(montant !== 0 && { montant }),
        },
        isComplet(),
      );
    }
  }, [disabled, numero, banque, montant, tireur, isComplet]);

  useUpdateEffect(() => {
    if (montant !== 0 && montant > reste && onError) {
      onError('Le montant ne peut pas être supérieur au reste');
    }
  }, [montant, reste]);

  return (
    <Form.Group style={{ marginTop: 0, marginBottom: 0 }}>
      <Form.Input
        disabled={disabled}
        autoFocus={!disabled}
        value={numero}
        label="N°"
        inline
        placeholder="12345"
        style={{ width: 100 }}
        onChange={(e, { value }) => handleChange(value, setNumero)}
        input={{ 'data-testid': 'numeroCheque' }}
        required
      />
      <BanqueDropdown
        disabled={disabled}
        value={banque}
        onChange={(value) => handleChange(value, setBanque)}
        data-testid="banqueCheque"
        required
      />
      <Form.Input
        disabled={disabled}
        value={tireur}
        label="Tireur"
        inline
        placeholder=""
        onChange={(e, { value }) => handleChange(value, setTireur)}
        onKeyDown={handleTireurKeyDown}
        input={{ 'data-testid': 'tireurCheque' }}
        icon={{
          name: 'id card',
          basic: true,
          link: true,
          size: 'large',
          fitted: true,
          style: { width: 40 },
          tabIndex: 0,
          onClick: () => {
            handleChange(payeur, setTireur);
          },
          onKeyDown: handleIconKeyDown,
          title: 'Reprendre le nom du payeur',
          'data-testid': 'iconPayeurCheque',
        }}
        required
      />
      <Montant
        data-testid="montantCheque"
        disabled={disabled}
        reste={reste}
        montant={montant}
        onChange={setMontant}
      />
    </Form.Group>
  );
};

export default Cheque;
